import { Col, Form, Input, Row, type FormInstance } from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  PRODUCT_UOM_DESCRIPTION_MAX_LENGTH,
  PRODUCT_UOM_NAME_MAX_LENGTH,
  createProductUomCodeRules,
  generateProductUomCodeFromName,
  sanitizeProductUomCodeInput,
} from "./productUomCodeValidation";

export interface ProductUomAddEditFormValues {
  uomName: string;
  uomCode: string;
  uomDescription: string;
}

interface MasterDataProductUomAddEditFormProps {
  form: FormInstance<ProductUomAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: ProductUomAddEditFormValues;
  onSubmit: (values: ProductUomAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const MasterDataProductUomAddEditForm = ({
  form,
  mode,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: MasterDataProductUomAddEditFormProps) => {
  const isCodeEditedManuallyRef = useRef(false);
  const uomCodeDisabled = useMemo(() => mode === "edit", [mode]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const uomName = values.uomName?.trim() ?? "";
    const uomCode = values.uomCode?.trim() ?? "";
    const uomDescription = values.uomDescription?.trim() ?? "";

    const hasRequiredValues =
      uomName !== "" &&
      uomDescription !== "" &&
      (mode === "edit" || uomCode !== "");

    const isDirty =
      mode === "create"
        ? true
        : uomName !== (initialValues.uomName?.trim() ?? "") ||
          uomDescription !== (initialValues.uomDescription?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    isCodeEditedManuallyRef.current = mode === "edit";
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "UOM name is required" },
      {
        max: PRODUCT_UOM_NAME_MAX_LENGTH,
        message: `UOM name must be less than or equal to ${PRODUCT_UOM_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const descriptionRules = useMemo(
    () => [
      { required: true, message: "UOM description is required" },
      {
        max: PRODUCT_UOM_DESCRIPTION_MAX_LENGTH,
        message: `UOM description must be less than or equal to ${PRODUCT_UOM_DESCRIPTION_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const uomCodeRules = useMemo(() => createProductUomCodeRules(), []);

  const syncUomCodeFromName = (valueOrEvent: unknown) => {
    if (uomCodeDisabled || isCodeEditedManuallyRef.current) return;
    form.setFieldsValue({
      uomCode: generateProductUomCodeFromName(getInputValue(valueOrEvent)),
    });
    form.validateFields(["uomCode"]).catch(() => {});
  };

  const handleUomNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      uomName: getInputValue(valueOrEvent),
    });
    syncUomCodeFromName(valueOrEvent);
    form.validateFields(["uomName"]).catch(() => {});
    updateFormValidity();
  };

  const handleUomCodeChange = (valueOrEvent: unknown) => {
    if (uomCodeDisabled) return;
    isCodeEditedManuallyRef.current = true;
    form.setFieldsValue({
      uomCode: sanitizeProductUomCodeInput(getInputValue(valueOrEvent)),
    });
    form.validateFields(["uomCode"]).catch(() => {});
    updateFormValidity();
  };

  const handleUomDescriptionChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      uomDescription: getInputValue(valueOrEvent),
    });
    form.validateFields(["uomDescription"]).catch(() => {});
    updateFormValidity();
  };

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-product-uom-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="uomName"
              label="UOM Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input
                placeholder="UOM name"
                onChange={handleUomNameChange}
                onInput={handleUomNameChange}
                onKeyUp={handleUomNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="uomCode"
              label="UOM Code"
              validateTrigger={["onChange", "onBlur"]}
              rules={uomCodeDisabled ? undefined : uomCodeRules}
              helpText={
                uomCodeDisabled
                  ? "Once UOM code is created, it cannot be changed."
                  : undefined
              }
            >
              <Input
                placeholder="UOM-CODE"
                isDisabled={uomCodeDisabled}
                onChange={handleUomCodeChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item
              name="uomDescription"
              label="UOM Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={descriptionRules}
            >
              <Input
                placeholder="UOM description"
                onChange={handleUomDescriptionChange}
                onInput={handleUomDescriptionChange}
                onKeyUp={handleUomDescriptionChange}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
